// frontend/src/pages/BinReport.js
// Bin Inventory Report — modern card-based warehouse tool

import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../App';

// ── Inline Quantity Editor ───────────────────────────────────────────────────
function QtyEditor({ sku, quantity, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue]     = useState(quantity);
  const [saving, setSaving]   = useState(false);

  async function save(newQty) {
    if (newQty === quantity || newQty < 0) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.patch(`/products/${encodeURIComponent(sku)}/quantity`, { quantity: newQty });
      onUpdate(sku, newQty);
    } catch { }
    setSaving(false);
    setEditing(false);
  }

  function adjust(delta) {
    const newQty = Math.max(0, quantity + delta);
    save(newQty);
  }

  if (editing) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={e => e.stopPropagation()}>
        <input type="number" value={value} min="0"
          onChange={e => setValue(parseInt(e.target.value) || 0)}
          onKeyDown={e => { if (e.key === 'Enter') save(value); if (e.key === 'Escape') setEditing(false); }}
          autoFocus
          style={{ width: 52, padding: '4px 6px', fontSize: 14, fontWeight: 700, textAlign: 'center',
            fontFamily: 'monospace', borderRadius: 6, border: '1px solid var(--accent)' }} />
        <button onClick={() => save(value)} disabled={saving}
          style={{ padding: '4px 8px', fontSize: 12, borderRadius: 6, border: '1px solid var(--accent)',
            background: 'var(--accent)', color: '#fff', cursor: 'pointer' }}>
          {saving ? '...' : 'OK'}
        </button>
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }} onClick={e => e.stopPropagation()}>
      <button onClick={() => adjust(-1)} disabled={saving || quantity === 0}
        style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid var(--border)',
          background: 'transparent', color: 'var(--danger)', fontSize: 16, fontWeight: 700,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          touchAction: 'manipulation' }}>
        -
      </button>
      <span onClick={() => { setEditing(true); setValue(quantity); }}
        style={{ minWidth: 28, textAlign: 'center', fontWeight: 700, fontSize: 14,
          color: 'var(--text)', cursor: 'pointer', padding: '2px 4px' }}>
        {saving ? '...' : quantity}
      </span>
      <button onClick={() => adjust(1)} disabled={saving}
        style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid var(--border)',
          background: 'transparent', color: 'var(--accent2)', fontSize: 16, fontWeight: 700,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          touchAction: 'manipulation' }}>
        +
      </button>
    </span>
  );
}

// ── Inline Price Editor ─────────────────────────────────────────────────────
function PriceEditor({ sku, price, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue]     = useState(price || 0);
  const [saving, setSaving]   = useState(false);

  async function save(e) {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    const newPrice = parseFloat(value) || 0;
    if (newPrice === price) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.patch(`/products/${encodeURIComponent(sku)}/price`, { price: newPrice });
      onUpdate(sku, newPrice);
    } catch (err) {
      console.error('Price update failed:', err);
    }
    setSaving(false);
    setEditing(false);
  }

  if (editing) {
    return (
      <form onSubmit={save} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
        onClick={e => e.stopPropagation()}>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>$</span>
        <input type="number" step="0.01" min="0" value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Escape') setEditing(false); }}
          autoFocus
          style={{ width: 72, padding: '4px 6px', fontSize: 13, textAlign: 'right',
            fontFamily: 'monospace', borderRadius: 6, border: '1px solid var(--accent)' }} />
        <button type="submit" disabled={saving}
          style={{ padding: '4px 8px', fontSize: 12, borderRadius: 6, border: '1px solid var(--accent)',
            background: 'var(--accent)', color: '#fff', cursor: 'pointer' }}>
          {saving ? '...' : 'OK'}
        </button>
      </form>
    );
  }

  return (
    <span onClick={e => { e.stopPropagation(); setEditing(true); setValue(price || 0); }}
      style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent2)', cursor: 'pointer',
        borderBottom: '1px dashed rgba(0,212,170,.3)' }}
      title="Click to edit price">
      {price > 0 ? `$${parseFloat(price).toFixed(2)}` : '$--'}
    </span>
  );
}

// ── Product Card (within a bin) ─────────────────────────────────────────────
function ProductCard({ item, onQuantityUpdate, onPriceUpdate, onRelisted }) {
  const { sku, title, quantity, price, pending, remaining, age_months, stale, images, listed_at } = item;
  const listedLabel = listed_at
    ? new Date(listed_at).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
    : null;
  const isEmpty = remaining === 0 && pending > 0;
  const isLow   = remaining === 1 && pending > 0;
  const [relistPhase, setRelistPhase] = useState(null); // null, 'researching', 'optimizing', 'relisting'
  const [relistResult, setRelistResult] = useState(null);

  let src = null;
  try { src = JSON.parse(images || '[]')[0] || null; } catch {}

  // No confirm dialog: this is tapped dozens of times in a row while working a
  // bin, and a modal per tap was the slowest part of the loop. The spinner is
  // what tells you the tap registered, so it has to show immediately.
  async function handleRelist() {
    if (relistPhase) return;               // ignore double-taps mid-run
    setRelistResult(null);
    setRelistPhase('researching');

    // One request does research, the AI rewrite and the relist, so there is no
    // per-phase signal to follow. The labels advance on a timer instead, which
    // is honest about roughly where it is and keeps the button from looking
    // stuck on a call that runs for a while.
    const timers = [
      setTimeout(() => setRelistPhase('optimizing'), 4000),
      setTimeout(() => setRelistPhase('relisting'), 12000),
    ];

    try {
      const res = await api.post(`/products/${encodeURIComponent(sku)}/ai-relist`);
      const research = res.data.research;
      setRelistResult({
        ok: true, title: res.data.newTitle, newSku: res.data.sku,
        research: research ? `${research.listingsChecked} listings researched, ${research.specsCount} specs found` : null,
      });
      if (onRelisted) onRelisted(sku, res.data);
    } catch (err) {
      setRelistResult({ ok: false, error: err.response?.data?.error || err.message });
    } finally {
      timers.forEach(clearTimeout);
      setRelistPhase(null);
    }
  }

  const relistLabel = relistPhase === 'researching' ? 'Researching...'
    : relistPhase === 'optimizing' ? 'AI Optimizing...'
    : relistPhase === 'relisting' ? 'Relisting...'
    : 'Research & Relist';

  const dotColor = isEmpty ? 'var(--danger)' : isLow ? 'var(--warn)' : 'var(--accent2)';

  return (
    <div style={{
      background: 'var(--surface2)',
      border: stale ? '1px solid rgba(255,214,10,.25)' : '1px solid var(--border)',
      borderRadius: 10,
      overflow: 'hidden',
      transition: 'border-color .2s',
    }}>
      {/* Image */}
      <div style={{ position: 'relative' }}>
        {src
          ? <img src={src} alt="" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block', background: 'var(--bg)' }} />
          : <div style={{ width: '100%', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--bg)', color: 'var(--muted)' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
            </div>
        }
        {/* Status badge (EMPTY/LOW/stale age) */}
        {(isEmpty || isLow || stale) && (
          <div style={{
            position: 'absolute', top: 6, right: 6,
            padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
            background: isEmpty ? 'rgba(255,77,109,.9)' : isLow ? 'rgba(255,214,10,.9)' : 'rgba(255,214,10,.7)',
            color: isEmpty ? '#fff' : '#000',
            backdropFilter: 'blur(8px)',
          }}>
            {isEmpty ? 'EMPTY' : isLow ? 'LOW' : `${age_months}mo`}
          </div>
        )}
        {/* Listed-date badge — always shown (non-stale items only; stale uses the right-side badge) */}
        {listedLabel && !stale && (
          <div title={`Listed ${new Date(listed_at).toLocaleDateString()}`} style={{
            position: 'absolute', top: 6, left: 6,
            padding: '2px 7px', borderRadius: 6, fontSize: 10, fontWeight: 600,
            background: 'rgba(0,0,0,.55)', color: '#fff',
            backdropFilter: 'blur(8px)',
          }}>
            {listedLabel}{age_months != null ? ` · ${age_months}mo` : ''}
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: '10px 12px 12px' }}>
        {/* Price */}
        <div style={{ marginBottom: 4 }}>
          <PriceEditor sku={sku} price={price} onUpdate={onPriceUpdate} />
        </div>

        {/* Title */}
        <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.35, marginBottom: 8,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          minHeight: 32 }}>
          {relistResult?.ok ? relistResult.title : (title || '--')}
        </div>

        {/* SKU */}
        <div style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--muted)', marginBottom: 8,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {relistResult?.newSku || sku}
        </div>

        {/* Quantity controls */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <QtyEditor sku={sku} quantity={quantity} onUpdate={onQuantityUpdate} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
            {pending > 0 && (
              <>
                <span style={{ color: 'var(--warn)', fontSize: 11 }}>-{pending}</span>
                <span style={{ color: 'var(--muted)', fontSize: 10 }}>=</span>
                <span style={{ fontWeight: 700, fontSize: 13, color: dotColor }}>{remaining}</span>
              </>
            )}
          </div>
        </div>

        {/* Relist button — available on every item, not just stale */}
        {!relistResult?.ok && (
          <button onClick={handleRelist} disabled={!!relistPhase}
            className={relistPhase ? 'eu-busy' : undefined}
            aria-busy={!!relistPhase}
            style={{ marginTop: 8, width: '100%', padding: '7px', fontSize: 11, fontWeight: 600, borderRadius: 6,
              border: '1px solid var(--accent)', background: relistPhase ? 'rgba(108,99,255,.2)' : 'rgba(108,99,255,.1)',
              color: 'var(--accent)', cursor: relistPhase ? 'wait' : 'pointer', touchAction: 'manipulation',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'all .2s' }}>
            {relistPhase && <span className="eu-spinner" />}
            {relistLabel}
          </button>
        )}
        {relistResult?.ok && (
          <div style={{ marginTop: 6, textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: 'var(--accent2)', fontWeight: 600 }}>Relisted</div>
            {relistResult.research && (
              <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 2 }}>{relistResult.research}</div>
            )}
          </div>
        )}
        {relistResult && !relistResult.ok && (
          <div style={{ marginTop: 4, fontSize: 10, color: 'var(--danger)' }}>{relistResult.error}</div>
        )}
      </div>
    </div>
  );
}

// ── Bin Section ─────────────────────────────────────────────────────────────
function BinSection({ bin, onQuantityUpdate, onPriceUpdate, onRelisted }) {
  const { location, total_qty, pending_qty, remaining_after, stale_count, skus } = bin;
  const binEmpty = remaining_after === 0 && pending_qty > 0;
  const binLow   = remaining_after <= 2 && pending_qty > 0 && !binEmpty;
  const [collapsed, setCollapsed] = useState(false);

  const accentColor = binEmpty ? 'var(--danger)' : binLow ? 'var(--warn)' : stale_count > 0 ? 'var(--warn)' : 'var(--accent)';

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Bin header */}
      <div onClick={() => setCollapsed(!collapsed)}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 18px', borderRadius: collapsed ? 10 : '10px 10px 0 0',
          background: 'var(--surface)',
          borderLeft: `4px solid ${accentColor}`,
          border: `1px solid var(--border)`,
          borderLeftWidth: 4,
          borderLeftColor: accentColor,
          cursor: 'pointer',
          transition: 'border-radius .2s',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Collapse arrow */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            style={{ transition: 'transform .2s', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0)' }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
          <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 20, color: accentColor }}>
            {location}
          </span>
          <span style={{ fontSize: 12, color: 'var(--muted)', background: 'rgba(255,255,255,.06)',
            padding: '2px 10px', borderRadius: 99, fontWeight: 600 }}>
            {skus.length} item{skus.length !== 1 ? 's' : ''}
          </span>
          {binEmpty && <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 99,
            background: 'rgba(255,77,109,.15)', color: 'var(--danger)' }}>Empty After Pick</span>}
          {binLow && !binEmpty && <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 99,
            background: 'rgba(255,214,10,.15)', color: 'var(--warn)' }}>Low</span>}
          {stale_count > 0 && <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 99,
            background: 'rgba(255,214,10,.1)', color: 'var(--warn)' }}>{stale_count} stale</span>}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontWeight: 700, fontSize: 20, color: 'var(--text)' }}>{total_qty}</span>
            {pending_qty > 0 && (
              <span style={{ fontSize: 13, color: 'var(--muted)', marginLeft: 6 }}>
                <span style={{ color: dotColor(binEmpty, binLow), fontWeight: 600 }}>{remaining_after}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Product grid */}
      {!collapsed && (
        <div style={{
          background: 'var(--bg)', border: '1px solid var(--border)', borderTop: 'none',
          borderRadius: '0 0 10px 10px', padding: 14,
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 12,
          }}>
            {skus.map(item => (
              <ProductCard key={item.sku} item={item}
                onQuantityUpdate={onQuantityUpdate} onPriceUpdate={onPriceUpdate} onRelisted={onRelisted} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function dotColor(isEmpty, isLow) {
  return isEmpty ? 'var(--danger)' : isLow ? 'var(--warn)' : 'var(--accent2)';
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function BinReport() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]       = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [filter, setFilter]   = useState('all');
  const [letterTab, setLetterTab] = useState('all');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/reports/bin-inventory').then(r => { setData(r.data); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  function handleQuantityUpdate(sku, newQty) {
    setData(prev => {
      if (!prev) return prev;
      const bins = prev.bins.map(bin => ({
        ...bin,
        skus: bin.skus.map(s => s.sku === sku ? { ...s, quantity: newQty, remaining: Math.max(0, newQty - s.pending) } : s),
      })).map(bin => ({
        ...bin,
        total_qty: bin.skus.reduce((s, p) => s + p.quantity, 0),
        remaining_after: bin.skus.reduce((s, p) => s + p.remaining, 0),
      }));
      return { ...prev, bins };
    });
  }

  function handlePriceUpdate(sku, newPrice) {
    setData(prev => {
      if (!prev) return prev;
      const bins = prev.bins.map(bin => ({
        ...bin,
        skus: bin.skus.map(s => s.sku === sku ? { ...s, price: newPrice } : s),
      }));
      return { ...prev, bins };
    });
  }

  function handleRelisted(oldSku, result) {
    setData(prev => {
      if (!prev) return prev;
      const bins = prev.bins.map(bin => ({
        ...bin,
        skus: bin.skus.map(s => s.sku === oldSku
          ? { ...s, sku: result.sku, title: result.newTitle, sku_year: String(new Date().getFullYear()).slice(-2), stale: false, age_months: 0 }
          : s),
        stale_count: bin.skus.filter(s => s.sku === oldSku ? false : s.stale).length,
      }));
      return { ...prev, bins };
    });
  }

  let bins = data?.bins || [];

  // Letter navigator groups — counted over ALL bins, independent of the active letter/filters.
  const letterCounts = {};
  (data?.bins || []).forEach(b => {
    const c = (b.location || '').charAt(0).toUpperCase();
    const key = /[A-Z]/.test(c) ? c : '#';
    letterCounts[key] = (letterCounts[key] || 0) + 1;
  });
  const letters = Object.keys(letterCounts).sort();

  if (filter === 'stale')   bins = bins.filter(b => b.stale_count > 0);
  if (filter === 'pending') bins = bins.filter(b => b.pending_qty > 0);
  if (filter === 'empty')   bins = bins.filter(b => b.remaining_after === 0 && b.pending_qty > 0);

  if (letterTab !== 'all') bins = bins.filter(b => {
    const c = (b.location || '').charAt(0).toUpperCase();
    return (/[A-Z]/.test(c) ? c : '#') === letterTab;
  });

  if (search) bins = bins.filter(b => b.location.toLowerCase().includes(search.toLowerCase()));

  if (productSearch) {
    const q = productSearch.toLowerCase();
    bins = bins.map(b => {
      const matched = b.skus.filter(s => s.sku.toLowerCase().includes(q) || (s.title || '').toLowerCase().includes(q));
      return matched.length > 0 ? { ...b, skus: matched } : null;
    }).filter(Boolean);
  }

  const totalStock = data?.summary?.total_stock || 0;
  const totalPending = data?.summary?.total_pending || 0;
  const totalAfter = data?.summary?.total_after || 0;
  const totalStale = data?.summary?.total_stale || 0;

  if (loading) return <div className="loading">Loading bin report...</div>;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-title">Bin Inventory</div>
        <button className="btn btn-ghost" onClick={load} style={{ gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
          </svg>
          Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'In Stock',    value: totalStock,   color: 'var(--text)' },
          { label: 'Picking',     value: totalPending,  color: 'var(--warn)' },
          { label: 'After Pick',  value: totalAfter,    color: 'var(--accent2)' },
          { label: 'Stale Items', value: totalStale,    color: totalStale > 0 ? 'var(--warn)' : 'var(--accent2)' },
        ].map(s => (
          <div key={s.label} style={{
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
            padding: '16px 14px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 26, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Search + Filters */}
      <div className="inv-toolbar">
        <div className="inv-search" style={{ maxWidth: 220 }}>
          <span className="inv-search-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Bin location..." />
        </div>
        <div className="inv-search" style={{ flex: 2 }}>
          <span className="inv-search-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </span>
          <input value={productSearch} onChange={e => setProductSearch(e.target.value)}
            placeholder="Search SKU or product..." />
        </div>

        <div className="inv-filter-group">
          {[['all','All'],['stale','Stale'],['pending','Picking'],['empty','Empty']].map(([k,l]) => (
            <button key={k} className={`inv-filter-btn ${filter===k?'active':''}`}
              onClick={() => setFilter(k)}>{l}</button>
          ))}
        </div>

        <span className="inv-results-count">{bins.length} bins</span>
      </div>

      {/* Letter navigator — one tap filters to a bin zone (e.g. all D bins) */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: 16,
        position: 'sticky', top: 0, zIndex: 5, background: 'var(--bg)', WebkitOverflowScrolling: 'touch' }}>
        <button onClick={() => setLetterTab('all')}
          className={`inv-filter-btn ${letterTab === 'all' ? 'active' : ''}`}
          style={{ flex: '0 0 auto' }}>All</button>
        {letters.map(L => (
          <button key={L} onClick={() => setLetterTab(L)}
            className={`inv-filter-btn ${letterTab === L ? 'active' : ''}`}
            style={{ flex: '0 0 auto', minWidth: 42 }}>
            {L}<span style={{ opacity: .5, fontSize: 10, marginLeft: 4 }}>{letterCounts[L]}</span>
          </button>
        ))}
      </div>

      {/* Bin list */}
      {bins.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize: 48, marginBottom: 12, opacity: .5 }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/>
            </svg>
          </div>
          <div>No bins found</div>
        </div>
      ) : (
        bins.map(bin => (
          <BinSection key={bin.location} bin={bin}
            onQuantityUpdate={handleQuantityUpdate} onPriceUpdate={handlePriceUpdate} onRelisted={handleRelisted} />
        ))
      )}
    </div>
  );
}
